import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { RotateCcw, Warehouse } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  dcLabel,
  dcUnits,
  type DcRow,
  type DcRowFilter,
  type DcSplit,
} from '@/components/WavePlanning/dc-model'
import {
  formatFullDate,
  toStoredPercents,
} from '@/components/WavePlanning/model'
import {
  toDcCenters,
  toDcRow,
  toDcTotals,
} from '@/components/WavePlanning/adapters'
import {
  WavePlanLoading,
  WavePlanLoadError,
  WavePlanPageShell,
} from '@/components/WavePlanning/WavePlanPageShell'
import { wavePlanPath } from '@/components/WavePlanning/routes'
import { DcAllocationPanel } from '@/components/WavePlanning/DcAllocationPanel'
import { DcSplitDialog } from '@/components/WavePlanning/DcAllocationDialogs'
import { DcWaveSelector } from '@/components/WavePlanning/DcWaveSelector'
import { DcWaveSummary } from '@/components/WavePlanning/DcWaveSummary'
import { DcVendorPoSummary } from '@/components/WavePlanning/DcVendorPoSummary'
import {
  isWavePlanConflict,
  useBulkWavePlanDc,
  useResetWavePlanDc,
  useUpdateWavePlanDcSplit,
  useWavePlan,
  useWavePlanDc,
} from '@/hooks/useWavePlans'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { useReturnFocus } from '@/hooks/useReturnFocus'
import '@/components/WavePlanning/dc-planning.css'

/**
 * A row's split over real DCs (virtual routing placeholders are never copied),
 * at the stored precision with its total kept: a split computed from quantity
 * overrides (33.333333%) must neither be rejected nor fall short of 100%.
 */
const splitOf = (row: DcRow, virtualDcIds: ReadonlySet<string>): DcSplit => {
  const cells = Object.entries(row.allocations).filter(
    ([dcId]) => !virtualDcIds.has(dcId)
  )
  const stored = toStoredPercents(cells.map(([, cell]) => cell.percent))
  return Object.fromEntries(cells.map(([dcId], index) => [dcId, stored[index]]))
}

const splitInputs = (split: DcSplit, virtualDcIds: ReadonlySet<string>) =>
  Object.entries(split)
    .filter(([dc_id]) => !virtualDcIds.has(dc_id))
    .map(([dc_id, percent]) => ({ dc_id, percent }))

export default function WavePlanningDistributionCenters() {
  const { planId = '' } = useParams()
  const detail = useWavePlan(planId)
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null)
  const dc = useWavePlanDc(planId, selectedWeek)
  const updateSplit = useUpdateWavePlanDcSplit(planId)
  const bulk = useBulkWavePlanDc(planId)
  const resetDc = useResetWavePlanDc(planId)
  const [filter, setFilter] = useState<DcRowFilter>('all')
  const [confirmReset, setConfirmReset] = useState(false)
  const focus = useReturnFocus()
  // The inputs version the copy draft was opened against (not the latest one).
  const [copy, setCopy] = useState<{
    row: DcRow
    inputsVersion: number
  } | null>(null)
  const [notice, setNotice] = useState('')

  const data = dc.data
  const centers = useMemo(() => (data ? toDcCenters(data) : []), [data])
  // Routing placeholders ("Single DC Strategy"): shown, never edited or copied.
  const virtualDcIds = useMemo(
    () =>
      new Set(
        (data?.centers ?? [])
          .filter(center => center.virtual)
          .map(center => center.id)
      ),
    [data]
  )
  const realDcCount = centers.filter(dc => !virtualDcIds.has(dc.id)).length
  /** "DC-EAST (East DC)" in the notices. */
  const centerLabel = (dcId: string) => {
    const center = centers.find(item => item.id === dcId)
    return center ? dcLabel(center) : ''
  }
  const waveRows = useMemo(
    () =>
      data?.week
        ? data.rows.filter(row => row.wave_qty > 0).map(row => toDcRow(row))
        : [],
    [data]
  )

  if (detail.isError || (dc.isError && !data))
    return (
      <WavePlanLoadError
        error={detail.error ?? dc.error}
        onRetry={() => {
          void detail.refetch()
          void dc.refetch()
        }}
      />
    )
  if (!detail.data) return <WavePlanLoading />
  const plan = detail.data
  // Edits wait while the DC view itself reloads (other views do not matter).
  const editable =
    plan.permissions.can_edit && !dc.isFetching && !dc.isPlaceholderData

  if (!data?.week)
    return (
      <WavePlanPageShell
        detail={plan}
        title="Distribution Center Planning"
        compact
      >
        {dc.isLoading ? (
          <WavePlanLoading label="Loading distribution centers…" />
        ) : (
          <Card className="flex flex-col items-center gap-3 px-6 py-14 text-center shadow-none">
            <Warehouse size={30} className="text-muted-foreground" />
            <h2 className="text-sm font-semibold">
              No wave dates to distribute
            </h2>
            <p className="max-w-md text-xs text-muted-foreground">
              Add a wave in the overall plan, then assign SKU quantities before
              distributing them to DCs.
            </p>
            <Button asChild size="sm">
              <Link to={wavePlanPath(plan.id)}>Go to overall plan</Link>
            </Button>
          </Card>
        )}
      </WavePlanPageShell>
    )

  const wave = data.week
  const waveLabel = formatFullDate(wave.date)
  const copyRow = copy?.row ?? null
  const copyTargets = copyRow?.otherWeekIds ?? []
  /** One DC's percent for several lines of the wave; ``scope`` names them in the notice. */
  const setDcPercent = (
    lines: DcRow[],
    dcId: string,
    value: number,
    scope = ''
  ) =>
    bulk.mutate(
      {
        body: {
          op: 'set_dc_percent',
          line_ref_ids: lines.map(row => row.id),
          week_id: wave.id,
          dc_id: dcId,
          percent: value,
        },
      },
      {
        onSuccess: result =>
          setNotice(
            `${dcUnits(value)}% applied to ${result.applied} SKU lines${scope} for ${centerLabel(dcId)} in ${waveLabel}.`
          ),
      }
    )

  return (
    <div className="dc-planning">
      <WavePlanPageShell
        detail={plan}
        title="Distribution Center Planning"
        subtitle={`${realDcCount} ${realDcCount === 1 ? 'DC serves' : 'DCs serve'} this plan's stores`}
        compact
      >
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            disabled={!editable || resetDc.isPending}
            onClick={() => {
              focus.remember()
              setConfirmReset(true)
            }}
          >
            <RotateCcw size={14} />
            Reset DC changes
          </Button>
        </div>
        <DcWaveSummary
          totals={toDcTotals(data.totals)}
          allWeeksTotals={toDcTotals(data.all_weeks_totals)}
          lineCount={waveRows.length}
          waveLabel={waveLabel}
          splitIssueCount={waveRows.filter(row => !row.balanced).length}
          filter={filter}
          onFilter={setFilter}
        />
        <DcWaveSelector
          weeks={data.weeks}
          selectedId={wave.id}
          onSelect={setSelectedWeek}
        />
        <DcAllocationPanel
          waveId={wave.id}
          waveNumber={data.weeks.findIndex(item => item.id === wave.id) + 1}
          waveLabel={waveLabel}
          rows={waveRows}
          centers={centers}
          virtualDcIds={virtualDcIds}
          vendorTotals={data.vendor_totals}
          core={plan.calc_path === 'CORE'}
          readOnly={!editable}
          filter={filter}
          onFilterChange={setFilter}
          onEdit={(row, dcId, value) =>
            updateSplit.mutate({
              lineRefId: row.id,
              weekId: wave.id,
              dcId,
              percent: value,
            })
          }
          onEditAll={(dcId, value) => setDcPercent(waveRows, dcId, value)}
          onEditGroup={(name, lines, dcId, value) =>
            setDcPercent(lines, dcId, value, ` of ${name}`)
          }
          onCopy={row => {
            focus.remember()
            setCopy({ row, inputsVersion: data.inputs_version })
          }}
        />
        <p
          role="status"
          aria-live="polite"
          className="text-xs text-muted-foreground"
        >
          {notice}
        </p>
        <DcVendorPoSummary
          centers={centers}
          wave={data.vendor_totals ?? []}
          allWaves={data.all_weeks_vendor_totals ?? []}
          waveLabel={waveLabel}
        />
        {copy && copyRow && (
          <DcSplitDialog
            centers={centers.filter(
              center =>
                center.id in copyRow.allocations && !virtualDcIds.has(center.id)
            )}
            count={copyTargets.length}
            initial={splitOf(copyRow, virtualDcIds)}
            description={`${copyRow.line.sku} · ${copyRow.line.type}: ${copyTargets.length} other waves with units.`}
            pending={bulk.isPending}
            onClose={() => setCopy(null)}
            onCloseAutoFocus={focus.restore}
            onApply={split =>
              bulk.mutate(
                {
                  body: {
                    op: 'apply_split',
                    line_ref_ids: [copyRow.id],
                    target_week_ids: copyTargets,
                    splits: splitInputs(split, virtualDcIds),
                  },
                  inputsVersion: copy.inputsVersion,
                },
                {
                  onSuccess: () => {
                    setNotice(
                      `DC split copied to ${copyTargets.length} other waves for ${copyRow.line.sku} ${copyRow.line.type}.`
                    )
                    setCopy(null)
                  },
                  // A stale draft cannot be retried; other errors keep it open.
                  onError: error => {
                    if (isWavePlanConflict(error)) setCopy(null)
                  },
                }
              )
            }
          />
        )}
        <ConfirmationDialog
          open={confirmReset}
          onOpenChange={setConfirmReset}
          title="Reset DC changes"
          description="Every SKU-week goes back to the default DC split. This applies to all waves of this plan."
          confirmText="Reset"
          cancelText="Cancel"
          variant="destructive"
          isLoading={resetDc.isPending}
          onCloseAutoFocus={focus.restore}
          onConfirm={() =>
            resetDc.mutate(undefined, {
              onSuccess: () => {
                setConfirmReset(false)
                setNotice('DC percentages reset for this wave plan.')
              },
            })
          }
        />
      </WavePlanPageShell>
    </div>
  )
}
